import { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { ChartContainer } from '../../../../../common/chart/chartContainer'
import Bar from '../../../../../common/chart/chartContainer/Bar'
import Line from '../../../../../common/chart/chartContainer/Line'
import { LegendItem } from '../../../../../common/chart/legend/LegendItem'
import { Icon } from '../../../../../common/html/icon'
import { SCREEN } from '../../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../../common/selectCurrencyUnit/useFormatCurrency'
import { Tooltip } from '../../../../../common/tooltip'
import { getUnique } from '../../../../../helpers/common'
import {
  convertData,
  formatVal,
  isInValidValue,
  valToPercent,
} from '../../../../../helpers/value'
import { selectUserType } from '../../../../../layout/store/slice'
import { USER_TYPE } from '../../../../../utils/authorization'
import {
  getNoDataComponent,
  getStyleHeightContainer,
  getValueAndTitleCheckbox,
  handleIconDownload,
} from '../../helper'
import { changeChecked, selectChecked } from '../../store/slice'
import { TREND_FUNDING_DATA, TREND_RATIO_KEY } from './constant'
import { selectDataViewByTrend, selectIndicatorById } from './store/slice'

const Trend = ({ id, isDynamicReport, check, isDownloadComponent }) => {
  const chartId = useRef(getUnique())

  const dispatch = useDispatch()
  const locale = useSelector((state) => state.i18n.locale)
  const userType = useSelector(selectUserType)
  const { getExchangeRate, multiplier, formatValueWithUnit, decimalLength } =
    useFormatCurrency(SCREEN.FINANCIAL_ANALYSIS)
  const dataByTrend = useSelector(selectDataViewByTrend)
  const indicatorById = useSelector(selectIndicatorById)
  const checkedStore = useSelector(selectChecked(id))

  const checked = checkedStore || check

  const setChecked = (checked) => dispatch(changeChecked({ id, checked }))

  const dataConvert = convertData(
    dataByTrend,
    getExchangeRate,
    indicatorById,
    multiplier,
    ['year'],
    ['FA087', 'FA088', 'RT0020'],
  )

  const checkboxs = TREND_FUNDING_DATA.map((item) => ({
    ...item,
    ...getValueAndTitleCheckbox(indicatorById, item.value),
  }))

  const bars = checkboxs.filter(
    (item) => item.isBarChart && checked.includes(item.value),
  )
  const groupStackBarKeys = bars.map((bar) => bar.value)

  const lines = checkboxs.filter(
    (item) => item.isLineChart && checked.includes(item.value),
  )

  const isNoData = !dataConvert.length

  useEffect(() => {
    handleIconDownload(id, isNoData)
  }, [isNoData])

  if (isNoData) {
    return getNoDataComponent(isDynamicReport)
  }

  const isNotHasBar = !bars.length

  const chart = []
  if (!isDynamicReport || !isDownloadComponent) {
    chart.push(...[1, 2])
  } else {
    if (checked.includes(TREND_RATIO_KEY)) {
      chart.push(1)
    }
    if (checkboxs.some(({ value }) => checked.includes(value))) {
      chart.push(2)
    }
  }

  const isH100 = chart.length === 2

  return (
    <div
      style={{
        height: isDownloadComponent
          ? '100%'
          : getStyleHeightContainer(
              isDynamicReport,
              isNoData,
              isH100 ? 332 : 332 / 2,
            ),
      }}
    >
      {chart.includes(1) && (
        <div className={isH100 ? 'h50 pb1' : 'h100'}>
          <div className="h100 flexColumn">
            <div className="fw600 pb1">
              <Translate value="financialAnalysis.CURRENT_RATIO" />
            </div>
            <ChartContainer
              data={dataConvert}
              width="100%"
              height="100%"
              margin={{
                top: 10,
                bottom: 0,
                left: isDownloadComponent ? 26 : 0,
                right: 0,
              }}
              xAxis={[
                {
                  dataKey: 'year',
                  tickMargin: 3,
                  height: 20,
                },
              ]}
              yAxis={[
                {
                  yAxisId: 'left',
                  dataKeys: [TREND_RATIO_KEY],
                  isBarChart: true,
                  unit: '%',
                },
              ]}
              tooltip={{
                cursor: false,
                schema: {
                  content: [
                    {
                      dataKey: TREND_RATIO_KEY,
                      label: indicatorById[TREND_RATIO_KEY]?.ratioName,
                      formatValue: (value) => formatVal(value, locale) + '%',
                    },
                  ],
                },
              }}
            >
              <Bar
                yAxisId="left"
                dataKey={TREND_RATIO_KEY}
                fill="transparent"
                label={<CustomizedLabel fill="#006AA6" />}
                showLabelEmpty={() => {
                  return false
                }}
              />
            </ChartContainer>
          </div>
        </div>
      )}
      {chart.includes(2) && (
        <div className={isH100 ? 'h50 pt1 dFlex' : 'h100 dFlex'}>
          <div className="flexColumn w60 h100">
            <div className="fw600 pb1">
              <Translate value="financialAnalysis.SHORT_AND_LONG_TEARM_FUNDING" />
            </div>
            <ChartContainer
              chartId={chartId.current}
              data={dataConvert}
              margin={{
                top: 10,
                bottom: 0,
                left: isNotHasBar ? 16 : isDownloadComponent ? 26 : 0,
                right: isDownloadComponent ? 26 : 0,
              }}
              width="100%"
              height="100%"
              xAxis={[
                {
                  dataKey: 'year',
                  tickMargin: 3,
                  height: 20,
                  tickFormatter: (year) => '‘' + String(year).slice(2, 4),
                },
              ]}
              yAxis={[
                {
                  yAxisId: 'left',
                  dataKeys: bars.map((item) => item.value),
                  isBarChart: true,
                  hide: isNotHasBar,
                  decimalLength,
                },
                {
                  yAxisId: 'right',
                  orientation: 'right',
                  dataKeys: lines.map((item) => item.value),
                  isLineChart: true,
                  tickFormatter: (value) => formatVal(value, locale) + '%',
                },
              ]}
              tooltip={{
                schema: {
                  content: [
                    ...bars.map((bar) => ({
                      dataKey: bar.value,
                      label: bar.title,
                      formatValue: (value) => formatValueWithUnit({ value }),
                    })),
                    ...lines.map((line) => ({
                      dataKey: line.value,
                      label: line.title,
                      formatValue: (value) => valToPercent(value, locale, true),
                    })),
                  ],
                },
              }}
            >
              {bars.map((bar, barIndex) => (
                <Bar
                  key={bar.value}
                  yAxisId="left"
                  stackId={'stackBar'}
                  maxBarSize={25}
                  dataKey={bar.value}
                  fill={bar.color}
                  showLabelEmpty={({ index }) =>
                    barIndex === 0 &&
                    groupStackBarKeys.every((dataKey) =>
                      isInValidValue(dataConvert[index][dataKey]),
                    )
                  }
                />
              ))}
              {lines.map((line) => (
                <Line
                  key={line.value}
                  yAxisId="right"
                  dataKey={line.value}
                  stroke={line.color}
                  strokeWidth={2}
                  isAnimationActive={false}
                />
              ))}
            </ChartContainer>
          </div>
          <div className="h100 w40 pl1 flexColumn justifyContentCenter">
            {checkboxs.map((item, index) => (
              <div
                className="dFlex alignItemsCenter"
                key={item.value}
                style={{ marginTop: index ? 4 : 0 }}
              >
                <LegendItem
                  title={
                    <>
                      {item.title}
                      {!isDynamicReport &&
                        !isDownloadComponent &&
                        item.tooltipContent && (
                          <Tooltip
                            elementName="span"
                            tooltipContent={item.tooltipContent}
                          >
                            <Icon
                              className="icon-info-circle ml1"
                              style={{ color: '#9C9C9C', marginTop: 2 }}
                            />
                          </Tooltip>
                        )}
                    </>
                  }
                  isI18n={false}
                  value={item.value}
                  color={item.color}
                  isCheckbox={userType?.userTypeId === USER_TYPE.PREMIUM}
                  dataKey={item.value}
                  chartId={chartId.current}
                  listCheckBox={checked}
                  setListCheckBox={setChecked}
                  isDynamicReport={isDynamicReport || isDownloadComponent}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const CustomizedLabel = (props) => {
  const {
    x,
    y,
    width,
    fill,
    index,
    value,
    barProps: { data },
  } = props

  const background = data[index].background

  if (isInValidValue(value)) {
    return (
      <line
        x1={background.x + background.width / 2}
        y1={background.y}
        x2={background.x + background.width / 2}
        y2={background.y + background.height}
        stroke={fill}
      />
    )
  }

  return (
    <>
      <line
        x1={background.x + background.width / 2}
        y1={background.y}
        x2={background.x + background.width / 2}
        y2={background.y + background.height}
        stroke={fill}
      />
      <circle cx={x + width / 2} cy={y} r="4" fill={fill} />
    </>
  )
}

export default Trend
